import React, { useState, useEffect } from 'react';
import { ExternalLink, Filter, Loader } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getImageUrlWithFallback } from '../utils/imageUtils';

const CanvaTemplateGallery = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStyle, setSelectedStyle] = useState('all');
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const styles = [
    { value: 'all', label: 'Tutti' },
    { value: 'classico', label: 'Classico' },
    { value: 'moderno', label: 'Moderno' },
    { value: 'vintage', label: 'Vintage' },
    { value: 'rustico', label: 'Rustico' },
    { value: 'elegante', label: 'Elegante' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/canva-templates/?active_only=true`);
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i template",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = selectedStyle === 'all' 
    ? templates 
    : templates.filter(t => t.style === selectedStyle);

  const getStyleColor = (style) => {
    const colors = {
      classico: 'bg-blue-100 text-blue-800',
      moderno: 'bg-purple-100 text-purple-800',
      vintage: 'bg-amber-100 text-amber-800',
      rustico: 'bg-green-100 text-green-800',
      elegante: 'bg-pink-100 text-pink-800'
    };
    return colors[style] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
          <ExternalLink className="w-6 h-6 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Template Canva</h3>
        <p className="text-sm text-gray-600 mt-1">
          Scegli un template professionale e personalizzalo su Canva
        </p>
      </div>

      {/* Style Filter */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtra per stile:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {styles.map((style) => (
            <button
              key={style.value}
              onClick={() => setSelectedStyle(style.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedStyle === style.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mb-3" />
          <p className="text-sm text-gray-600">Caricamento template...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            {selectedStyle === 'all' 
              ? 'Nessun template disponibile' 
              : 'Nessun template trovato per questo stile'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div 
              key={template.id} 
              className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-lg transition-all group"
            >
              {/* Thumbnail - Always Square */}
              <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden relative">
                {template.thumbnailUrl ? (
                  <img 
                    src={getImageUrlWithFallback(template.thumbnailUrl)} 
                    alt={template.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="text-gray-400 text-center p-4">
                          <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                          </svg>
                          <p class="text-sm">Template Canva</p>
                        </div>
                      `;
                    }}
                  />
                ) : (
                  <div className="text-gray-400 text-center p-4">
                    <ExternalLink className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-sm">Template Canva</p>
                  </div>
                )}
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all" />
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-900 text-sm">{template.name}</h4>
                  <span className={`px-2 py-1 text-xs rounded ${getStyleColor(template.style)}`}>
                    {template.style}
                  </span>
                </div>
                
                {template.description && (
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                    {template.description}
                  </p>
                )}

                {/* Open Button */}
                <a
                  href={template.canvaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Apri in Canva
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          📌 <strong>Come funziona:</strong> Clicca su un template per aprirlo in Canva. 
          Personalizzalo con i tuoi dati, scaricalo e caricalo nella sezione "Carica File" per vedere l'anteprima 3D!
        </p>
      </div>
    </div>
  );
};

export default CanvaTemplateGallery;
